<template>
  <div class="flex flex-col gap-3 sm:gap-6">
    <div v-if="showTemplateSelector">
      <SelectMenu
        :initial-value="selectedTemplate?.name || 'Choose a template'"
        :options="templates.map(t => t.name)"
        @selected-option="(templateName: string) => setTemplate(templateName)"
      />
      <router-link :to="templateLibraryPath" target="_blank">
        <Button class="text-indigo-600 p-0" variant="link">Template Library</Button>
      </router-link>
    </div>

    <div>
      <Label class="text-base sm:text-xl pb-1 sm:pb-2">Message</Label>
      <TextareaMergeTagInput
        ref="messageInput"
        :placeholder="placeholder"
        :model-value="props.modelValue"
        @update:model-value="(value: string) => emit('update:modelValue', value)"
      />
    </div>
    <MergeTagSelect
      :label="null"
      :float-dropdown="true"
      :pretty-tags="false"
      @selected-tag="tag => messageInput?.insertMergeTagAtCursor(tag)"
    />
  </div>
  <div v-if="isScheduled">
    <Label class="text-base sm:text-xl mb-1 sm:mb-2 pt-4">Send On</Label>
    <DatePicker
      v-model="invokeOn as string"
      granularity="hour"
      disable-past
      @update:model-value="
        value => {
          emit('update:invoke-on', `${value}`);
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import TextareaMergeTagInput from './merge_tag/textarea_input/TextareaMergeTagInput.vue';
import MergeTagSelect from './merge_tag/MergeTagSelect/index.vue';
import SelectMenu from './SelectMenu.vue';
import type { TextareaMergeTagInputExpose } from './merge_tag/textarea_input/TextareaMergeTagInput.vue';
import type { SmsTemplateEntity } from '@/api/entity_types/SmsTemplate';
import { listSmsTemplates } from '@/api/sms_templates';
import { DatePicker } from '@/components/ui/date_picker';

type Props = {
  modelValue?: string;
  showTemplateSelector?: boolean;
  placeholder?: string;
  isScheduled?: boolean;
  invokeOn?: string;
};

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  showTemplateSelector: true,
  placeholder: 'Enter your message...',
  isScheduled: false,
  invokeOn: '',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:invoke-on': [value: string];
}>();

const templates = ref<SmsTemplateEntity[]>([]);
const selectedTemplate = ref<SmsTemplateEntity | null>(null);
const messageInput = ref<TextareaMergeTagInputExpose | null>(null);
const templateLibraryPath = '/settings/templates/sms';

watch(
  () => props.modelValue,
  newValue => {
    if (newValue === '') messageInput.value?.setContent('');
  }
);

const fetchTemplates = async (): Promise<void> => {
  const response = await listSmsTemplates();
  templates.value = response.records;
};

const setTemplate = (templateName: string): void => {
  const template = templates.value.find(t => t.name === templateName);
  if (!template) return;
  selectedTemplate.value = template;
  messageInput.value?.setContent(template.body);
  emit('update:modelValue', template.body);
};

onMounted(async () => {
  if (props.showTemplateSelector) await fetchTemplates();
});
</script>
